import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { kycApi, adminApi } from '../services/api';
import { KycDocument } from '../types/customer';
import { DocumentStatus } from '../types/statuses';
import { getBackendDocumentType, getFrontendDocumentType } from '../utils/documentTypeMap';

export interface Document {
  id: string;
  name: string;
  url: string;
  status: 'pending_approval' | 'approved' | 'rejected';
  rejectionReason?: string;
}

export interface DocumentRequest {
  _id: string;
  type: string;
  status: 'pending' | 'approved' | 'rejected';
  uploadedAt: string;
  s3Url: string;
  userId: string;
}

export interface UploadResult {
  documentType: string;
  success: boolean;
  error?: string;
  document?: KycDocument;
}

export interface DocumentUploadState {
  documents: Document[];
  documentRequests: DocumentRequest[];
  loading: boolean;
  error: string | null;
  uploadProgress: number;
  uploadSuccess: boolean;
  batchUploadResults: UploadResult[];
  batchUploading: boolean;
}

const initialState: DocumentUploadState = {
  documents: [],
  documentRequests: [],
  loading: false,
  error: null,
  uploadProgress: 0,
  uploadSuccess: false,
  batchUploadResults: [],
  batchUploading: false,
};

// Async thunk to fetch user uploaded documents
export const fetchUserDocuments = createAsyncThunk<
  Document[],
  void,
  { rejectValue: { message: string } }
>('documentUpload/fetchUserDocuments', async (_, { rejectWithValue }) => {
  try {
    const response = await kycApi.getMyDocuments();
    // Map KycDocument[] to Document[]
    const docs: Document[] = response.documents.map((doc: KycDocument) => ({
      id: doc._id,
      name: getFrontendDocumentType(doc.type), // Map backend type to frontend type
      url: doc.s3Key ? `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.amazonaws.com/${doc.s3Key}` : '', // Construct URL from s3Key with environment variable bucket name
      status: doc.status === 'pending' ? 'pending_approval' : doc.status, // Map 'pending' to 'pending_approval'
      rejectionReason: doc.rejectionReason,
    }));
    return docs;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Failed to fetch documents' });
  }
});

// Async thunk to upload document
export const uploadDocument = createAsyncThunk<
  KycDocument,
  { file: File; documentType: string },
  { rejectValue: { message: string } }
>('documentUpload/uploadDocument', async ({ file, documentType }, { rejectWithValue }) => {
  try {
    const backendDocumentType = getBackendDocumentType(documentType);

    const { uploadUrl, s3Key, bucketName } = await kycApi.getUploadUrl({
      documentType: backendDocumentType,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    // Upload file to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    // Confirm upload and save document metadata in database
    const confirmResponse = await kycApi.confirmUpload({
      s3Key,
      bucketName,
      documentType: backendDocumentType,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    // Return a KycDocument object constructed from the response
    return {
      _id: confirmResponse.data.documentId,
      type: backendDocumentType,
      s3Key,
      status: confirmResponse.data.status as DocumentStatus,
      uploadedAt: confirmResponse.data.uploadedAt,
    };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Upload failed' });
  }
});

// Async thunk to fetch document requests for admin
export const fetchDocumentRequests = createAsyncThunk<
  DocumentRequest[],
  void,
  { rejectValue: { message: string } }
>('documentUpload/fetchDocumentRequests', async (_, { rejectWithValue }) => {
  try {
    const response = await adminApi.getPendingDocuments();
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Failed to fetch document requests' });
  }
});

// Async thunk to approve document
export const approveDocument = createAsyncThunk<
  string,
  string,
  { rejectValue: { message: string } }
>('documentUpload/approveDocument', async (documentId, { rejectWithValue }) => {
  try {
    await adminApi.approveDocument(documentId);
    return documentId;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Approve failed' });
  }
});

// Async thunk to reject document
export const rejectDocument = createAsyncThunk<
  string,
  { documentId: string; reason: string },
  { rejectValue: { message: string } }
>('documentUpload/rejectDocument', async ({ documentId, reason }, { rejectWithValue }) => {
  try {
    await adminApi.rejectDocument(documentId, reason);
    return documentId;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Reject failed' });
  }
});

// Async thunk to upload all documents in batch
export const uploadAllDocuments = createAsyncThunk<
  UploadResult[],
  { documents: { file: File; documentType: string }[] },
  { rejectValue: { message: string } }
>('documentUpload/uploadAllDocuments', async ({ documents }, { rejectWithValue }) => {
  try {
    // Upload all documents in parallel using Promise.allSettled
    const uploadPromises = documents.map(async ({ file, documentType }) => {
      try {
        const backendDocumentType = getBackendDocumentType(documentType);

        const { uploadUrl, s3Key, bucketName } = await kycApi.getUploadUrl({
          documentType: backendDocumentType,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        });

        // Upload file to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        // Confirm upload and save document metadata in database
        const confirmResponse = await kycApi.confirmUpload({
          s3Key,
          bucketName,
          documentType: backendDocumentType,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        });

        // Return success result
        return {
          documentType,
          success: true,
          document: {
            _id: confirmResponse.data.documentId,
            type: backendDocumentType,
            s3Key,
            status: confirmResponse.data.status as DocumentStatus,
            uploadedAt: confirmResponse.data.uploadedAt,
          },
        } as UploadResult;
      } catch (error: any) {
        return {
          documentType,
          success: false,
          error: error.response?.data?.message || error.message || 'Upload failed',
        } as UploadResult;
      }
    });

    const results = await Promise.allSettled(uploadPromises);
    const uploadResults: UploadResult[] = results.map(result =>
      result.status === 'fulfilled' ? result.value : result.reason
    );

    return uploadResults;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Batch upload failed' });
  }
});

// Async thunk to reupload a rejected document
export const reuploadDocument = createAsyncThunk<
  KycDocument,
  { file: File; documentType: string; existingDocumentId: string },
  { rejectValue: { message: string } }
>('documentUpload/reuploadDocument', async ({ file, documentType, existingDocumentId }, { rejectWithValue }) => {
  try {
    const backendDocumentType = getBackendDocumentType(documentType);

    const { uploadUrl, s3Key, bucketName } = await kycApi.getUploadUrl({
      documentType: backendDocumentType,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      isReupload: true,
    });

    // Upload file to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
    }

    // Confirm upload and save document metadata in database
    const confirmResponse = await kycApi.confirmUpload({
      s3Key,
      bucketName,
      documentType: backendDocumentType,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    // Return a KycDocument object constructed from the response
    return {
      _id: confirmResponse.data.documentId,
      type: backendDocumentType,
      s3Key,
      status: confirmResponse.data.status as DocumentStatus,
      uploadedAt: confirmResponse.data.uploadedAt,
    };
  } catch (err: any) {
    return rejectWithValue(err.response?.data || { message: 'Reupload failed' });
  }
});

const documentUploadSlice = createSlice({
  name: 'documentUpload',
  initialState,
  reducers: {
    clearDocumentUploadError(state) {
      state.error = null;
    },
    resetUploadStatus(state) {
      state.uploadSuccess = false;
      state.uploadProgress = 0;
    },
    clearBatchUploadResults(state) {
      state.batchUploadResults = [];
      state.batchUploading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDocuments.fulfilled, (state, action: PayloadAction<Document[]>) => {
        state.loading = false;
        state.documents = action.payload;
      })
      .addCase(fetchUserDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch documents';
      })
      .addCase(uploadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.uploadSuccess = false;
        state.uploadProgress = 0;
      })
      .addCase(uploadDocument.fulfilled, (state) => {
        state.loading = false;
        state.uploadSuccess = true;
        state.uploadProgress = 100;
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as { message: string })?.message || 'Upload failed';
        state.uploadSuccess = false;
        state.uploadProgress = 0;
      })
      .addCase(fetchDocumentRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDocumentRequests.fulfilled, (state, action: PayloadAction<DocumentRequest[]>) => {
        state.loading = false;
        state.documentRequests = action.payload;
      })
      .addCase(fetchDocumentRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch document requests';
      })
      .addCase(approveDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveDocument.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        const idx = state.documentRequests.findIndex(req => req._id === action.payload);
        if (idx !== -1) {
          state.documentRequests[idx].status = 'approved';
        }
      })
      .addCase(approveDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to approve document';
      })
      .addCase(rejectDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectDocument.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        const idx = state.documentRequests.findIndex(req => req._id === action.payload);
        if (idx !== -1) {
          state.documentRequests[idx].status = 'rejected';
        }
      })
      .addCase(rejectDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to reject document';
      })
      .addCase(uploadAllDocuments.pending, (state) => {
        state.batchUploading = true;
        state.error = null;
        state.batchUploadResults = [];
      })
      .addCase(uploadAllDocuments.fulfilled, (state, action: PayloadAction<UploadResult[]>) => {
        state.batchUploading = false;
        state.batchUploadResults = action.payload;
      })
      .addCase(uploadAllDocuments.rejected, (state, action) => {
        state.batchUploading = false;
        state.error = action.payload?.message || 'Batch upload failed';
      })
      .addCase(reuploadDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(reuploadDocument.fulfilled, (state, action: PayloadAction<KycDocument>) => {
        state.loading = false;
        // Update the document in the documents array
        const idx = state.documents.findIndex(doc => doc.id === action.payload._id);
        if (idx !== -1) {
          state.documents[idx] = {
            id: action.payload._id,
            name: getFrontendDocumentType(action.payload.type),
            url: action.payload.s3Key ? `https://${process.env.REACT_APP_S3_BUCKET_NAME}.s3.amazonaws.com/${action.payload.s3Key}` : '',
            status: action.payload.status === 'pending' ? 'pending_approval' : action.payload.status,
          };
        }
      })
      .addCase(reuploadDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Reupload failed';
      });
  },
});

export const { clearDocumentUploadError, resetUploadStatus, clearBatchUploadResults } = documentUploadSlice.actions;

export default documentUploadSlice.reducer;
